import 'core-js/es/array';
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  FilterCategory,
  RangeFilterCategory,
  MultiOptionCategory,
  FilterListPayload,
  RangePayload,
  CardListFilterState,
  CategoryFilterSelection,
  CardProps,
  BaseOption,
  FilterTypes,
} from './types';
import { isRangeFilter, isMultiFilter, isFilterCategory } from './utils';
import axios from 'axios';
import { hasCookie } from '@utils/cookie';

const toggleChoice = createAsyncThunk<
  CategoryFilterSelection,
  CategoryFilterSelection,
  { state: CardListFilterState }
>('filters/toggleChoice', async (payload: CategoryFilterSelection) => {
  const data = await axios.get(
    `/umbraco/api/toursapi/GetChoiceSelection?cat=${payload.categoryIndex}&opt=${payload.optionIndex}`
  );
  return JSON.parse(data.data);
});

const updatePrice = createAsyncThunk<
  any,
  RangePayload,
  { state: CardListFilterState }
>('filters/updatePrice', async (payload: RangePayload) => {
  const data = await axios.get(
    `/umbraco/api/toursapi/GetRangeSelection?cat=${payload.index}&min=${payload.min}&max=${payload.max}`
  );

  return JSON.parse(data.data);
});

const clear = createAsyncThunk('filters/clear', async () => {
  const data = new Promise<void>((resolve) => {
    return resolve();
  });

  return data;
});

const abTestKey = 'ab_newTab';

const initialState: CardListFilterState = {
  isLoading: true,
  translations: {
    now: 'Now',
    viewTour: 'View Tour',
    more: 'Show more',
    less: 'Show less',
    clear: 'Clear filters',
    close: 'Close',
    apply: 'Apply',
  },
  cards: [],
  selectedCards: [],
  openCardsInNewTab: false,
  showAllCards: false,
  filters: [],
  isFilterVisibleOnSmall: false,
  isProcessing: false,
};

const filterListSlice = createSlice({
  name: 'filterList',
  initialState: initialState,
  reducers: {
    setInitData: {
      reducer: (state, action: PayloadAction<FilterListPayload>) => {
        const { cards, filters, translations, id, currency } = action.payload;
        var updatedCards = cards.map((x) => {
          return { ...x, priceData: { ...x.priceData, currency: currency } };
        });

        if (filters) {
          filters.forEach((el) => {
            let filterObj: FilterCategory = {
              id: el.id,
              type: el.type,
              name: el.name,
              icon: el.icon,
              closed: false,
              showAll: false,
            };

            switch (el.type) {
              case FilterTypes.Price:
                const maxVal = Math.max.apply(
                  Math,
                  cards.map((i) => {
                    return i.priceData.price;
                  })
                );
                const minVal = Math.min.apply(
                  Math,
                  cards.map((i) => {
                    return i.priceData.price;
                  })
                );

                let rangeFilterObj: RangeFilterCategory = {
                  ...filterObj,
                  min: minVal,
                  activeMin: minVal,
                  max: maxVal,
                  activeMax: maxVal,
                  icon: el.icon,
                };
                state.filters.push(rangeFilterObj);
                break;
              case FilterTypes.Month:
              case FilterTypes.Checkbox:
                let multiFilterObj: MultiOptionCategory = {
                  ...filterObj,
                  options: [],
                };
                multiFilterObj.options = initFilterOptions(
                  el.options,
                  updatedCards
                );
                state.filters.push(multiFilterObj);
                break;
            }
          });
        }

        if (hasCookie(abTestKey)) {
          state.openCardsInNewTab = true;
        }

        state.cards.push(...updatedCards);
        state.selectedCards.push(...updatedCards.map((x) => x.id));
        state.translations = translations;
        state.isLoading = false;
        state.sectionId = id;
      },
      prepare: (payload: string) => {
        return {
          payload: JSON.parse(payload),
        };
      },
    },
    // Sets state for loader
    setLoadingState: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    // Toggle if category is visible
    toggleCategoryVisibility: (state, action: PayloadAction<number>) => {
      let currentFilter = state.filters[action.payload];
      if (isFilterCategory(currentFilter)) {
        currentFilter.closed = !currentFilter.closed;
        state.filters[action.payload] = currentFilter;
      }
    },
    // Toggle if all options in category are visible
    toggleOptionsVisibility: (state, action: PayloadAction<number>) => {
      let currentFilter = state.filters[action.payload];
      if (isFilterCategory(currentFilter)) {
        currentFilter.showAll = !currentFilter.showAll;
        state.filters[action.payload] = currentFilter;
      }
    },
    toggleFilterVisibilityOnSmall: (state) => {
      state.isFilterVisibleOnSmall = !state.isFilterVisibleOnSmall;
    },
    toggleShowAllCards: (state) => {
      state.showAllCards = !state.showAllCards;

      // AB test code
      if (!state.openCardsInNewTab && hasCookie(abTestKey)) {
        state.openCardsInNewTab = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(toggleChoice.pending, (state, action) => {
        if (!state.isProcessing) {
          state.isProcessing = true;
        }
      })
      .addCase(toggleChoice.rejected, (state, action) => {
        if (state.isProcessing) {
          state.isProcessing = false;
        }
      })
      .addCase(toggleChoice.fulfilled, (state, action) => {
        if (state.isProcessing) {
          const { categoryIndex, optionIndex } = action.payload;

          const currentFilter = state.filters[categoryIndex];
          if (isMultiFilter(currentFilter)) {
            currentFilter.options[optionIndex].selected =
              !currentFilter.options[optionIndex].selected;
            state.selectedCards = filterCardsData(state);
            updateFilters(state);
          }
          state.isProcessing = false;

          // AB test code
          if (!state.openCardsInNewTab && hasCookie(abTestKey)) {
            state.openCardsInNewTab = true;
          }
        }
      })
      .addCase(updatePrice.pending, (state, action) => {
        if (!state.isProcessing) {
          state.isProcessing = true;
        }
      })
      .addCase(updatePrice.rejected, (state, action) => {
        if (state.isProcessing) {
          state.isProcessing = false;
        }
      })
      .addCase(updatePrice.fulfilled, (state, action) => {
        if (state.isProcessing) {
          const { min, max, index } = action.payload;
          let currentFilter = state.filters[index];
          if (isRangeFilter(currentFilter)) {
            currentFilter.activeMin = min;
            currentFilter.activeMax = max;
            state.filters[index] = currentFilter;
            state.selectedCards = filterCardsData(state);
            state.filters = updateFilters(state);
          }
          state.isProcessing = false;

          // AB test code
          if (!state.openCardsInNewTab && hasCookie(abTestKey)) {
            state.openCardsInNewTab = true;
          }
        }
      })
      .addCase(clear.pending, (state, action) => {
        if (!state.isProcessing) {
          state.isProcessing = true;
        }
      })
      .addCase(clear.rejected, (state, action) => {
        if (state.isProcessing) {
          state.isProcessing = false;
        }
      })
      .addCase(clear.fulfilled, (state, action) => {
        if (state.isProcessing) {
          state.selectedCards = state.cards.map((x) => x.id);
          for (let fi = 0; fi < state.filters.length; fi++) {
            switch (state.filters[fi].type) {
              case FilterTypes.Price:
                let rangeFilterObj = state.filters[fi];
                if (isRangeFilter(rangeFilterObj)) {
                  rangeFilterObj.activeMax = rangeFilterObj.max;
                  rangeFilterObj.activeMin = rangeFilterObj.min;
                  state.filters[fi] = rangeFilterObj;
                }
                break;
              case FilterTypes.Month:
              case FilterTypes.Checkbox:
                let multiOptionObj = state.filters[fi];
                if (isMultiFilter(multiOptionObj)) {
                  multiOptionObj.options = initFilterOptions(
                    multiOptionObj.options,
                    state.cards
                  );
                  state.filters[fi] = multiOptionObj;
                }
                break;
            }
          }
          state.isProcessing = false;
        }

        // AB test code
        if (!state.openCardsInNewTab && hasCookie(abTestKey)) {
          state.openCardsInNewTab = true;
        }
      });
  },
});

function initFilterOptions(options: BaseOption[], cards: CardProps[]) {
  return options.map((option: BaseOption) => {
    const count = cards.filter((card) => {
      return card.params && card.params.includes(option.id);
    }).length;
    return {
      ...option,
      selected: false,
      disabled: count === 0,
      count: count,
    };
  });
}

function filterCardsData(state: CardListFilterState): number[] {
  let filteredCards = [...state.cards];

  state.filters.forEach((filter: RangeFilterCategory | MultiOptionCategory) => {
    switch (filter.type) {
      case FilterTypes.Price:
        if (isRangeFilter(filter)) {
          filteredCards = filteredCards.filter((el) => {
            if (el.priceData) {
              return (
                el.priceData.price >= filter.activeMin &&
                el.priceData.price <= filter.activeMax
              );
            } else {
              return false;
            }
          });
        }
        break;
      case FilterTypes.Month:
      case FilterTypes.Checkbox:
        if (isMultiFilter(filter)) {
          const selectedOptions = filter.options
            .filter((option) => option.selected)
            .map((option) => option.id);
          if (selectedOptions && selectedOptions.length > 0) {
            filteredCards = filteredCards.filter((el) => {
              return el.params.some((param) => selectedOptions.includes(param));
            });
          }
        }
        break;
    }
  });
  return filteredCards.map((card) => card.id);
}

function updateFilters(
  state: CardListFilterState
): (MultiOptionCategory | RangeFilterCategory)[] {
  for (let fi = 0; fi < state.filters.length; fi++) {
    const filter = state.filters[fi];
    switch (filter.type) {
      case FilterTypes.Month:
      case FilterTypes.Checkbox:
        if (isMultiFilter(filter)) {
          filter.options.forEach((option, index) => {
            const cardsCount = state.cards.filter((card) => {
              return (
                card.params &&
                card.params.includes(option.id) &&
                state.selectedCards.includes(card.id)
              );
            }).length;
            filter.options[index].disabled =
              cardsCount === 0 && !filter.options[index].selected;
            filter.options[index].count = cardsCount;
            state.filters[fi] = filter;
          });
        }
        break;
    }
  }
  return state.filters;
}

export const {
  setInitData,
  setLoadingState,
  toggleCategoryVisibility,
  toggleOptionsVisibility,
  toggleFilterVisibilityOnSmall,
  toggleShowAllCards,
} = filterListSlice.actions;

export { toggleChoice, updatePrice, clear };

export default filterListSlice.reducer;
